import { type DependencyList, useEffect, useRef } from "react";

export interface UseEventListenerOptions {
  runOnMount?: boolean;
  debounce?: number;
}

export type EventType = "resize" | "scroll";
export type Target = EventTarget | null;

export function useEventListener(
  event: EventType | EventType[],
  elements: Target | Target[],
  callback: () => void,
  deps?: DependencyList,
  options?: UseEventListenerOptions,
) {
  const animationFrame = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { runOnMount = false, debounce = 0 } = options ?? {};

  function runInAnimationFrame(fn: () => void) {
    if (animationFrame.current === 0) {
      animationFrame.current = window.requestAnimationFrame(() => {
        animationFrame.current = 0;
        fn();
      });
    }
  }

  useEffect(() => {
    if (runOnMount) {
      callback();
    }
  }, [runOnMount, callback]);

  useEffect(() => {
    function eventListener() {
      if (debounce > 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          runInAnimationFrame(callback);
        }, debounce);
      } else {
        runInAnimationFrame(callback);
      }
    }

    const events = Array.isArray(event) ? event : [event];
    const targets = Array.isArray(elements) ? elements : [elements];

    for (const el of targets) {
      if (el) {
        for (const evt of events) {
          el.addEventListener(evt, eventListener);
        }
      }
    }

    return () => {
      for (const el of targets) {
        if (el) {
          for (const evt of events) {
            el.removeEventListener(evt, eventListener);
          }
        }
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
        animationFrame.current = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
